import { useState, useRef, useEffect } from "react";
import { ContentCard as ContentCardUI } from "@/components/ui/content-card";
import CardThumbnail from "../content-card/CardThumbnail";
import { ContentItem } from "../content-card/types";
import { Tag } from "lucide-react";
import { useContentTags } from "@/hooks/useContentTags";
import AddTagButton from "@/components/common/AddTagButton";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import CardActionMenu from "../content-card/CardActionMenu";
import GridCardDeleteDialog from "../content-card/GridCardDeleteDialog";

interface FrameCardProps {
  item: ContentItem;
  onClick?: () => void;
  onEdit?: (id: string, currentTitle: string) => void;
  onDelete?: (id: string) => void;
  onArchive?: (id: string) => void;
}

const FrameCard = ({
  item,
  onClick,
  onEdit,
  onDelete,
  onArchive,
}: FrameCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTagsOverlay, setShowTagsOverlay] = useState(false);
  const [showTagSelectionDialog, setShowTagSelectionDialog] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const { contentTags, removeTag } = useContentTags(item.id);
  const { handleContentDragStart, handleContentDragEnd } = useDragAndDrop();
  
  useEffect(() => {
    const handleMouseLeave = () => {
      setIsHovering(false);
    };

    const currentRef = cardRef.current;
    if (currentRef) {
      currentRef.addEventListener("mouseleave", handleMouseLeave);
    }
    
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);
  
  const handleMouseEnter = () => {
    setIsHovering(true);
  };
  
  const handleCardClick = () => {
    if (onClick) {
      console.log(`[FRAME DEBUG] FrameCard - Card clicked: ${item.id} (${item.title})`);
      onClick();
    }
  };

  const handleThumbnailClick = (e: React.MouseEvent) => {
    console.log(`[FRAME DEBUG] FrameCard - Thumbnail clicked for item ${item.id}, opening URL: ${item.url}`);
    e.stopPropagation(); // Prevent card click
    window.open(item.url, "_blank", "noopener");
  };

  const handleDeleteRequest = (id: string) => {
    console.log(`[FRAME DEBUG] FrameCard - Delete requested for item ${id}`);
    setContentToDelete(id);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    console.log(`[FRAME DEBUG] FrameCard - Delete confirmed for item ${contentToDelete}`);
    
    if (contentToDelete && onDelete) {
      onDelete(contentToDelete);
    }
  };

  const handleDeleteDialogOpenChange = (open: boolean) => {
    setShowDeleteDialog(open);
  };

  const toggleTagsOverlay = (e: React.MouseEvent) => {
    console.log(`[FRAME DEBUG] FrameCard - Tags overlay toggled for item ${item.id}`);
    e.stopPropagation(); // Prevent card click
    setShowTagsOverlay(!showTagsOverlay);
  };

  const handleRemoveTag = (e: React.MouseEvent, tagId: string) => {
    console.log(`[FRAME DEBUG] FrameCard - Remove tag ${tagId} for item ${item.id}`);
    e.stopPropagation(); // Prevent card click
    removeTag(tagId);
  };

  const handleTagsOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent any parent handlers from capturing the event
  };
  
  const handleAddTagClick = () => {
    console.log(`[FRAME DEBUG] FrameCard - Add tag button clicked for item ${item.id}`);
    setShowTagSelectionDialog(true);
  };

  const handleDragStart = (e: React.DragEvent) => {
    const dragData = {
      contentId: item.id,
      contentTitle: item.title,
      currentCollectionId: item.collection_id
    };
    
    handleContentDragStart(e, dragData);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    handleContentDragEnd();
  };

  // Ensure we have a valid color for the card
  const cardColor = item.collection_color || "#6B7789";
  console.log(`[FRAME DEBUG] FrameCard - Using color ${cardColor} for item ${item.id}`);

  return (
    <>
      <div 
        ref={cardRef} 
        className="relative"
        onMouseEnter={handleMouseEnter}
        draggable={true}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <ContentCardUI 
          color={cardColor}
          onClick={handleCardClick}
          isClickable={!!onClick}
          fullHeight={true}
          withPadding={false}
        >
          <div className="flex flex-col h-full">
            <div className="relative flex-1 group">
              {isHovering && !showTagsOverlay && (
                <button 
                  className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-gray-900/20 text-white hover:bg-gray-900/30 transition-colors"
                  onClick={toggleTagsOverlay}
                >
                  <Tag size={18} />
                </button>
              )}
              
              <div 
                className="w-full rounded-t-lg overflow-hidden cursor-pointer"
                onClick={handleThumbnailClick}
              >
                <CardThumbnail 
                  thumbnailUrl={item.thumbnail_url} 
                  contentType={item.content_type}
                  title={item.title}
                />
              </div>
              
              {/* Hover overlay with title */}
              {isHovering && !showTagsOverlay && (
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
                  <h3 className="text-white font-medium text-base">
                    {item.title}
                  </h3>
                </div>
              )}
              
              {/* Tags overlay */}
              {showTagsOverlay && (
                <div 
                  className="absolute inset-0 bg-gray-900/70 rounded-t-lg z-20 p-3 overflow-y-auto"
                  onClick={handleTagsOverlayClick}
                >
                  <div className="flex justify-between items-center text-white mb-2">
                    <span className="font-medium">TAGS</span>
                    <div onClick={(e) => e.stopPropagation()}>
                      <AddTagButton 
                        variant="circle"
                        showHoverText={false}
                        contentId={item.id}
                        forceUseExternalDialog={true}
                        onAddClick={handleAddTagClick}
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {contentTags.length > 0 ? (
                      contentTags.map(tag => (
                        <div 
                          key={tag.id} 
                          className="flex items-center rounded-full border-2 pl-3 pr-1 py-0.5 text-white text-sm"
                          style={{ borderColor: tag.color }}
                        >
                          <span className="mr-1">{tag.name}</span>
                          <button 
                            className="p-0.5 hover:bg-gray-700/50 rounded-full"
                            onClick={(e) => handleRemoveTag(e, tag.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-white text-sm text-center w-full py-2">
                        No tags added yet
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Action menu - only visible on hover and not when tags are showing */}
              {isHovering && !showTagsOverlay && (
                <div className="absolute top-2 right-10 z-10">
                  <CardActionMenu
                    itemId={item.id}
                    itemTitle={item.title}
                    isArchived={item.archived}
                    onEdit={onEdit}
                    onArchive={onArchive}
                    onDelete={handleDeleteRequest}
                    variant="icon"
                    alignment="end"
                  />
                </div>
              )}
            </div>
          </div>
        </ContentCardUI>
      </div>
      
      <GridCardDeleteDialog
        open={showDeleteDialog}
        onOpenChange={handleDeleteDialogOpenChange}
        itemTitle={item.title}
        onConfirm={handleConfirmDelete}
      />
      
      <AddTagButton 
        contentId={item.id}
        isDialogOpen={showTagSelectionDialog}
        onDialogOpenChange={(open) => {
          setShowTagSelectionDialog(open);
          
          // Force a refresh when the tag dialog closes
          if (!open && showTagSelectionDialog) {
            setTimeout(() => {
              window.location.reload();
            }, 300);
          }
        }}
        className="hidden"
      />
    </>
  );
};

export default FrameCard;
